import { ControlTypes } from '../../../utils/helper';
import { STATUSES } from '../../../utils/Statuses';
import {
  getQuestionTypeConfig,
  isDisplayOnlyControl,
} from './questionTypeRegistry';
import { getQuestionPageId } from './formUtils';

export const LOGIC_ACTIONS = [
  { label: 'Show this question', value: 'show' },
  { label: 'Hide this question', value: 'hide' },
];

export const GROUP_COMBINE_OPTIONS = [
  { label: 'AND — every group must match', value: 'all' },
  { label: 'OR — any group can match', value: 'any' },
];

export const GROUP_MATCH_OPTIONS = [
  { label: 'AND — all conditions', value: 'all' },
  { label: 'OR — any condition', value: 'any' },
];

export const MAX_LOGIC_GROUPS = 2;
export const MAX_LOGIC_CONDITIONS_PER_GROUP = 3;

export const canAddLogicGroup = (groups) =>
  (groups?.length || 0) < MAX_LOGIC_GROUPS;

export const canAddLogicCondition = (rules) =>
  (rules?.length || 0) < MAX_LOGIC_CONDITIONS_PER_GROUP;

/** Preferred name ← legacy alias (eval + UI canonicalize). */
const OPERATOR_ALIASES = {
  is: 'equals',
  is_not: 'notEquals',
  answered: 'isNotEmpty',
  not_answered: 'isEmpty',
  not_contains: 'notContains',
  greater_than: 'greaterThan',
  less_than: 'lessThan',
};

const MATCH_CASE_OPS = new Set([
  'equals',
  'notEquals',
  'contains',
  'notContains',
  'startsWith',
  'endsWith',
  'in',
  'notIn',
]);

const op = (value, label, valueType = 'single', extras = {}) => ({
  value,
  label,
  valueType,
  supportsMatchCase: MATCH_CASE_OPS.has(value),
  ...extras,
});

const PRESENCE_OPERATORS = [
  op('isNotEmpty', 'is answered', 'none'),
  op('isEmpty', 'is not answered', 'none'),
];

const TEXT_OPERATORS = [
  op('equals', 'equals'),
  op('notEquals', 'does not equal'),
  op('contains', 'contains'),
  op('notContains', 'does not contain'),
  op('startsWith', 'starts with'),
  op('endsWith', 'ends with'),
  op('in', 'is any of', 'array'),
  op('notIn', 'is none of', 'array'),
  ...PRESENCE_OPERATORS,
];

const CHOICE_OPERATORS = [
  op('equals', 'equals'),
  op('notEquals', 'does not equal'),
  op('in', 'is any of', 'array'),
  op('notIn', 'is none of', 'array'),
  ...PRESENCE_OPERATORS,
];

const NUMBER_OPERATORS = [
  op('equals', 'equals'),
  op('notEquals', 'does not equal'),
  op('greaterThan', 'is greater than'),
  op('greaterThanOrEqual', 'is greater than or equal'),
  op('lessThan', 'is less than'),
  op('lessThanOrEqual', 'is less than or equal'),
  op('between', 'is between', 'range'),
  op('notBetween', 'is not between', 'range'),
  op('in', 'is any of', 'array'),
  op('notIn', 'is none of', 'array'),
  ...PRESENCE_OPERATORS,
];

const RELATIVE_DATE_OPERATORS = [
  op('today', 'is today', 'none'),
  op('thisMonth', 'is this month', 'none'),
  op('thisYear', 'is this year', 'none'),
];

const RELATIVE_DATE_OPERATOR_VALUES = new Set(
  RELATIVE_DATE_OPERATORS.map((item) => item.value),
);

const DATE_OPERATORS = [
  op('dateEquals', 'is on'),
  op('dateNotEquals', 'is not on'),
  op('greaterThan', 'is after'),
  op('greaterThanOrEqual', 'is on or after'),
  op('lessThan', 'is before'),
  op('lessThanOrEqual', 'is on or before'),
  op('between', 'is between', 'range'),
  op('notBetween', 'is not between', 'range'),
  ...RELATIVE_DATE_OPERATORS,
  ...PRESENCE_OPERATORS,
];

const ALL_OPERATOR_DEFS = [
  ...TEXT_OPERATORS,
  ...CHOICE_OPERATORS,
  ...NUMBER_OPERATORS,
  ...DATE_OPERATORS,
  ...PRESENCE_OPERATORS,
];

const OPERATOR_BY_VALUE = ALL_OPERATOR_DEFS.reduce((acc, item) => {
  if (!acc[item.value]) acc[item.value] = item;
  return acc;
}, {});

export const OPERATORS_WITHOUT_VALUE = [
  'isEmpty',
  'isNotEmpty',
  'answered',
  'not_answered',
  'today',
  'thisMonth',
  'thisYear',
];

const CHOICE_TYPES = new Set([
  ControlTypes.CHECKBOX,
  ControlTypes.RADIO,
  ControlTypes.DROPDOWN,
  ControlTypes.YES_NO,
]);

const NUMBER_TYPES = new Set([
  ControlTypes.NUMBER_INPUT,
  ControlTypes.RATING,
  ControlTypes.SLIDER,
  ControlTypes.SALARY,
]);

const DATE_TYPES = new Set([
  ControlTypes.DATE,
  ControlTypes.AVAILABILITY_RANGE,
]);

const PRESENCE_ONLY_TYPES = new Set([
  ControlTypes.FILE_UPLOAD,
  ControlTypes.MULTI_FILE,
  ControlTypes.SIGNATURE,
]);

const TEXT_TYPES = new Set([
  ControlTypes.TEXT_INPUT,
  ControlTypes.TEXTAREA,
  ControlTypes.EMAIL,
  ControlTypes.PHONE,
  ControlTypes.URL,
  ControlTypes.COUNTRY,
]);

export const canonicalizeOperator = (operator) =>
  OPERATOR_ALIASES[operator] || operator;

export const operatorNeedsValue = (operator) =>
  !OPERATORS_WITHOUT_VALUE.includes(operator) &&
  !OPERATORS_WITHOUT_VALUE.includes(canonicalizeOperator(operator));

export const getOperatorDef = (operator) =>
  OPERATOR_BY_VALUE[canonicalizeOperator(operator)] || null;

export const getOperatorValueType = (operator) =>
  getOperatorDef(operator)?.valueType || 'single';

export const operatorSupportsMatchCase = (operator) =>
  !!getOperatorDef(operator)?.supportsMatchCase;

export const getOperatorsForQuestion = (question) => {
  const type = question?.sectionType;
  if (!type) return TEXT_OPERATORS;
  if (CHOICE_TYPES.has(type)) return CHOICE_OPERATORS;
  if (NUMBER_TYPES.has(type)) return NUMBER_OPERATORS;
  if (DATE_TYPES.has(type)) return DATE_OPERATORS;
  if (PRESENCE_ONLY_TYPES.has(type)) return PRESENCE_OPERATORS;
  if (TEXT_TYPES.has(type)) return TEXT_OPERATORS;
  return TEXT_OPERATORS;
};

export const getDefaultOperatorForQuestion = (question) =>
  getOperatorsForQuestion(question)[0]?.value || 'equals';

export const getLogicValueOptions = (question) => {
  const opts = question?.sectionOptions?.opts || [];
  return opts
    .filter((option) => option?.label)
    .map((option) => ({ label: option.label, value: option.label }));
};

export const getQuestionLogic = (question) => question?.sectionOptions?.logic;

let logicIdSeq = 0;
const nextLogicId = (prefix) => {
  logicIdSeq += 1;
  return `${prefix}-${logicIdSeq}`;
};

export const createEmptyRule = () => ({
  id: nextLogicId('rule'),
  sourceOrder: null,
  operator: 'equals',
  value: '',
});

export const createEmptyGroup = () => ({
  id: nextLogicId('group'),
  match: 'all',
  rules: [createEmptyRule()],
});

export const getDefaultLogic = () => ({
  action: 'show',
  groupMatch: 'all',
  groups: [createEmptyGroup()],
  jumpToPageId: null,
});

export const normalizeLogic = (logic) => {
  if (!logic) return getDefaultLogic();

  if (Array.isArray(logic.groups) && logic.groups.length) {
    return {
      action: logic.action || 'show',
      groupMatch: logic.groupMatch || logic.match || 'all',
      groups: logic.groups.map((group) => ({
        id: group?.id,
        match: group?.match || 'all',
        rules: group?.rules?.length ? group.rules : [createEmptyRule()],
      })),
      jumpToPageId: logic.jumpToPageId || null,
    };
  }

  if (logic.rules?.length) {
    return {
      action: logic.action || 'show',
      groupMatch: logic.match || 'all',
      groups: [{ match: logic.match || 'all', rules: logic.rules }],
      jumpToPageId: logic.jumpToPageId || null,
    };
  }

  return getDefaultLogic();
};

const getConfiguredRules = (group) =>
  (group?.rules || []).filter((rule) => rule?.sourceOrder);

export const hasQuestionLogic = (question) => {
  const logic = getQuestionLogic(question);
  if (!logic) return false;
  const normalized = normalizeLogic(logic);
  return (
    normalized.groups.some((group) => getConfiguredRules(group).length > 0) ||
    !!normalized.jumpToPageId
  );
};

export const getLogicGroupCount = (question) => {
  const logic = getQuestionLogic(question);
  if (!logic) return 0;
  return normalizeLogic(logic).groups.filter(
    (group) => getConfiguredRules(group).length > 0,
  ).length;
};

const getOperatorLabel = (operator) =>
  getOperatorDef(operator)?.label || operator || 'equals';

const formatRuleValue = (raw) => {
  if (raw === null || raw === undefined || raw === '') return '""';
  if (Array.isArray(raw)) {
    const items = raw.map((item) => `"${item}"`).join(', ');
    return `[${items}]`;
  }
  return `"${raw}"`;
};

const describeRule = (rule, questions = []) => {
  const sourceQuestion = questions.find(
    (item) => Number(item?.sectionOrder) === Number(rule?.sourceOrder),
  );
  const label = sourceQuestion
    ? getQuestionLabel(sourceQuestion)
    : 'Question';
  const operator = getOperatorLabel(rule?.operator);
  if (!operatorNeedsValue(rule?.operator)) {
    return `"${label}" ${operator}`;
  }
  return `"${label}" ${operator} ${formatRuleValue(rule?.value)}`;
};

export const summarizeLogic = (logic, questions = []) => {
  const normalized = normalizeLogic(logic);
  const groupPhrases = normalized.groups
    .map((group) => {
      const rules = getConfiguredRules(group);
      if (!rules.length) return null;
      const joiner = group.match === 'any' ? ' or ' : ' and ';
      const phrase = rules
        .map((rule) => describeRule(rule, questions))
        .join(joiner);
      return rules.length > 1 && normalized.groups.length > 1
        ? `(${phrase})`
        : phrase;
    })
    .filter(Boolean);

  if (!groupPhrases.length) return '';
  const action = normalized.action === 'hide' ? 'Hide' : 'Show';
  const groupJoiner = normalized.groupMatch === 'any' ? ' or ' : ' and ';
  return `${action} if ${groupPhrases.join(groupJoiner)}`;
};

/** Condition text for rules that check one source question, e.g. Show if "Country" equals "Pakistan". */
export const describeSourceConditions = (logic, sourceOrder, questions = []) => {
  const order = Number(sourceOrder);
  if (!logic || !Number.isFinite(order)) return '';
  const normalized = normalizeLogic(logic);
  const phrases = normalized.groups
    .map((group) => {
      const rules = getConfiguredRules(group).filter(
        (rule) => Number(rule.sourceOrder) === order,
      );
      if (!rules.length) return null;
      const joiner = group.match === 'any' ? ' or ' : ' and ';
      return rules.map((rule) => describeRule(rule, questions)).join(joiner);
    })
    .filter(Boolean);

  if (!phrases.length) return '';
  const action = normalized.action === 'hide' ? 'Hide' : 'Show';
  const groupJoiner = normalized.groupMatch === 'any' ? ' or ' : ' and ';
  return `${action} if ${phrases.join(groupJoiner)}`;
};

/**
 * Questions selectable as a logic condition source for `question`.
 * Restricted to the same page: pages are answered sequentially, so a later
 * page's logic should only reference questions the candidate has already
 * seen on that same page, not earlier pages.
 */
export const getLogicSourceQuestions = (
  questions = [],
  question = null,
  pages = [],
) => {
  const excludedTypes = new Set([
    ControlTypes.SECTION,
    ControlTypes.HEADING,
    ControlTypes.SUB_HEADING,
    ControlTypes.PARAGRAPH,
    ControlTypes.DIVIDER,
    ControlTypes.STATEMENT,
    ControlTypes.SECTION_BREAK,
  ]);
  const targetPageId = getQuestionPageId(question, pages);

  return questions.filter(
    (item) =>
      !excludedTypes.has(item?.sectionType) &&
      !isDisplayOnlyControl(item) &&
      !item?.isSensitive &&
      item?.sectionOrder !== question?.sectionOrder &&
      Number(item?.sectionOrder) < Number(question?.sectionOrder) &&
      getQuestionPageId(item, pages) === targetPageId,
  );
};

export const getQuestionLabel = (question, fallbackIndex) => {
  if (question?.sectionLabel) return question.sectionLabel;
  const config = getQuestionTypeConfig(question);
  return `Untitled ${config?.sectionLabel || 'question'} ${
    fallbackIndex ? `(${fallbackIndex})` : ''
  }`.trim();
};

const isEmptyAnswer = (answer) => {
  if (answer === null || answer === undefined) return true;
  if (Array.isArray(answer)) return answer.length === 0;
  if (typeof answer === 'string') return answer.trim().length === 0;
  return false;
};

const toComparable = (answer) => {
  if (Array.isArray(answer)) return answer.map((item) => String(item ?? ''));
  if (answer === null || answer === undefined) return [];
  return [String(answer)];
};

const normalizeCase = (value, matchCase) =>
  matchCase ? String(value ?? '') : String(value ?? '').toLowerCase();

const parseDay = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : startOfDay(value);
  }
  const text = String(value).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const [year, month, day] = text.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const parseAvailabilityAnswer = (answer) => {
  if (answer == null || answer === '') return null;
  if (typeof answer === 'string') {
    const trimmed = answer.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return null;
      }
    }
    return trimmed;
  }
  return answer;
};

const getAnswerDays = (answer, sourceType) => {
  if (sourceType === ControlTypes.AVAILABILITY_RANGE) {
    const parsed = parseAvailabilityAnswer(answer);
    if (Array.isArray(parsed)) {
      return [parseDay(parsed[0]), parseDay(parsed[1])];
    }
    if (parsed && typeof parsed === 'object') {
      return [
        parseDay(parsed.from ?? parsed.start),
        parseDay(parsed.to ?? parsed.end),
      ];
    }
    const day = parseDay(parsed);
    return [day, day];
  }
  return [parseDay(answer)];
};

const everyAnswerDay = (answer, sourceType, predicate) => {
  const days = getAnswerDays(answer, sourceType);
  if (!days.length || days.some((day) => !day)) return false;
  return days.every((day) => predicate(day));
};

const asNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const asValueList = (value) => {
  if (!Array.isArray(value)) return null;
  return value;
};

const asRange = (value) => {
  if (!Array.isArray(value) || value.length !== 2) return null;
  return [value[0], value[1]];
};

const compareText = (left, right, matchCase, mode) => {
  const a = normalizeCase(left, matchCase);
  const b = normalizeCase(right, matchCase);
  if (mode === 'equals') return a === b;
  if (mode === 'contains') return a.includes(b);
  if (mode === 'startsWith') return a.startsWith(b);
  if (mode === 'endsWith') return a.endsWith(b);
  return false;
};

const sameMonthAs = (day, ref) =>
  day.getFullYear() === ref.getFullYear() && day.getMonth() === ref.getMonth();

const evaluateRelativeDate = (operator, answerDay, today = startOfDay(new Date())) => {
  if (!answerDay) return false;
  const day = startOfDay(answerDay);

  if (operator === 'today') return day.getTime() === today.getTime();
  if (operator === 'thisMonth') return sameMonthAs(day, today);
  if (operator === 'thisYear') return day.getFullYear() === today.getFullYear();

  return null;
};

const TEXT_OPERATOR_MODES = {
  equals: { mode: 'equals', negate: false },
  notEquals: { mode: 'equals', negate: true },
  contains: { mode: 'contains', negate: false },
  notContains: { mode: 'contains', negate: true },
  startsWith: { mode: 'startsWith', negate: false },
  endsWith: { mode: 'endsWith', negate: false },
};

const LIST_OPERATORS = new Set(['in', 'notIn']);
const BETWEEN_OPERATORS = new Set(['between', 'notBetween']);
const INEQUALITY_OPERATORS = new Set([
  'greaterThan',
  'greaterThanOrEqual',
  'lessThan',
  'lessThanOrEqual',
]);
const DATE_EQUALITY_OPERATORS = new Set(['dateEquals', 'dateNotEquals']);

const compareComparableText = (comparable, ruleValue, matchCase, mode, negate) => {
  const matched = comparable.some((item) =>
    compareText(item, ruleValue, matchCase, mode),
  );
  return negate ? !matched : matched;
};

const evaluateInList = (comparable, ruleValue, matchCase, negate) => {
  const list = asValueList(ruleValue);
  if (!list) return null;
  const matched = comparable.some((item) =>
    list.some((expected) => compareText(item, expected, matchCase, 'equals')),
  );
  return negate ? !matched : matched;
};

const evaluateDateBetween = (answer, sourceType, min, max, negate) => {
  let minDay = parseDay(min);
  let maxDay = parseDay(max);
  if (!minDay || !maxDay) return null;
  if (minDay > maxDay) [minDay, maxDay] = [maxDay, minDay];
  const inside = everyAnswerDay(
    answer,
    sourceType,
    (day) => day >= minDay && day <= maxDay,
  );
  return negate ? !inside : inside;
};

const evaluateNumberBetween = (answer, min, max, negate) => {
  const num = asNumber(answer);
  let minNum = asNumber(min);
  let maxNum = asNumber(max);
  if (num === null || minNum === null || maxNum === null) return null;
  if (minNum > maxNum) [minNum, maxNum] = [maxNum, minNum];
  const inside = num >= minNum && num <= maxNum;
  return negate ? !inside : inside;
};

const evaluateBetween = (operator, answer, sourceType, ruleValue) => {
  const range = asRange(ruleValue);
  if (!range) return null;
  const [min, max] = range;
  const negate = operator === 'notBetween';
  if (DATE_TYPES.has(sourceType)) {
    return evaluateDateBetween(answer, sourceType, min, max, negate);
  }
  return evaluateNumberBetween(answer, min, max, negate);
};

const compareDateBound = (answer, sourceType, expected, operator) => {
  if (operator === 'greaterThan') {
    return everyAnswerDay(answer, sourceType, (day) => day > expected);
  }
  if (operator === 'greaterThanOrEqual') {
    return everyAnswerDay(answer, sourceType, (day) => day >= expected);
  }
  if (operator === 'lessThan') {
    return everyAnswerDay(answer, sourceType, (day) => day < expected);
  }
  return everyAnswerDay(answer, sourceType, (day) => day <= expected);
};

const compareNumberBound = (num, expected, operator) => {
  if (operator === 'greaterThan') return num > expected;
  if (operator === 'greaterThanOrEqual') return num >= expected;
  if (operator === 'lessThan') return num < expected;
  return num <= expected;
};

const evaluateInequality = (operator, answer, sourceType, ruleValue) => {
  if (DATE_TYPES.has(sourceType)) {
    const expected = parseDay(ruleValue);
    if (!expected) return false;
    return compareDateBound(answer, sourceType, expected, operator);
  }
  const num = asNumber(answer);
  const expected = asNumber(ruleValue);
  if (num === null || expected === null) return false;
  return compareNumberBound(num, expected, operator);
};

const evaluateDateEquality = (operator, answer, sourceType, ruleValue) => {
  const expected = parseDay(ruleValue);
  if (!expected) return operator === 'dateNotEquals';
  const same = everyAnswerDay(
    answer,
    sourceType,
    (day) => day.getTime() === expected.getTime(),
  );
  return operator === 'dateEquals' ? same : !same;
};

const evaluateRelativeDateRule = (operator, answer, sourceType) => {
  const isRelative = RELATIVE_DATE_OPERATOR_VALUES.has(operator);
  if (!DATE_TYPES.has(sourceType) && !isRelative) return null;
  const days = getAnswerDays(answer, sourceType);
  if (!days.length || days.some((day) => !day)) return false;
  const results = days.map((day) => evaluateRelativeDate(operator, day));
  if (results.includes(null)) return null;
  return results.every((result) => result === true);
};

/**
 * @returns {boolean|null} null = skip rule (unknown / malformed)
 */
const evaluateRule = (rule, sourceQuestion, values) => {
  const operator = canonicalizeOperator(rule?.operator);
  if (!operator || !OPERATOR_BY_VALUE[operator]) return null;

  const answer = values?.[`q_${sourceQuestion?.id}`];
  const sourceType = sourceQuestion?.sectionType;
  const matchCase = rule?.matchCase !== false;
  const comparable = toComparable(answer);

  if (operator === 'isEmpty') return isEmptyAnswer(answer);
  if (operator === 'isNotEmpty') return !isEmptyAnswer(answer);

  const textOp = TEXT_OPERATOR_MODES[operator];
  if (textOp) {
    return compareComparableText(
      comparable,
      rule?.value,
      matchCase,
      textOp.mode,
      textOp.negate,
    );
  }

  if (LIST_OPERATORS.has(operator)) {
    return evaluateInList(
      comparable,
      rule?.value,
      matchCase,
      operator === 'notIn',
    );
  }

  if (BETWEEN_OPERATORS.has(operator)) {
    return evaluateBetween(operator, answer, sourceType, rule?.value);
  }

  if (INEQUALITY_OPERATORS.has(operator)) {
    return evaluateInequality(operator, answer, sourceType, rule?.value);
  }

  if (DATE_EQUALITY_OPERATORS.has(operator)) {
    return evaluateDateEquality(operator, answer, sourceType, rule?.value);
  }

  return evaluateRelativeDateRule(operator, answer, sourceType);
};

const allTrue = (results) => results.every((result) => result === true);
const anyTrue = (results) => results.includes(true);

const evaluateGroup = (group, values, questions) => {
  const rules = getConfiguredRules(group);
  if (!rules.length) return null;

  const results = rules
    .map((rule) => {
      const sourceQuestion = questions.find(
        (item) => Number(item?.sectionOrder) === Number(rule.sourceOrder),
      );
      if (!sourceQuestion) return true;
      return evaluateRule(rule, sourceQuestion, values);
    })
    .filter((result) => result !== null);

  if (!results.length) return null;

  return group?.match === 'any' ? anyTrue(results) : allTrue(results);
};

export const isQuestionVisible = (question, values = {}, questions = []) => {
  const logic = getQuestionLogic(question);
  if (!logic) return true;

  const normalized = normalizeLogic(logic);
  const groupResults = normalized.groups
    .map((group) => evaluateGroup(group, values, questions))
    .filter((result) => result !== null);

  if (!groupResults.length) return true;

  const matched =
    normalized.groupMatch === 'any'
      ? anyTrue(groupResults)
      : allTrue(groupResults);

  return normalized.action === 'hide' ? !matched : matched;
};

export const isQuestionAnswerable = (question, values = {}, questions = []) =>
  !question?.sectionOptions?.internalOnly &&
  isQuestionVisible(question, values, questions);

export const valuesFromQuestionAnswers = (questions = []) =>
  (questions || []).reduce((values, question) => {
    if (question?.id == null) return values;
    values[`q_${question.id}`] = question.answer;
    return values;
  }, {});

/** Submitted questions, keeping only those the saved answers still show. */
export const getVisibleAnsweredQuestions = (questions = []) => {
  const list = questions || [];
  const values = valuesFromQuestionAnswers(list);
  return list.filter((question) => isQuestionVisible(question, values, list));
};

/**
 * Answer payloads sometimes omit display logic. Copy it from the form
 * definition so a submitted preview can still hide unanswered branches.
 */
export const withQuestionLogic = (answered = [], definition = []) => {
  if (!definition?.length) return answered || [];
  const byId = new Map(definition.map((question) => [String(question?.id), question]));
  return (answered || []).map((question) => {
    const source = byId.get(String(question?.id));
    const logic = question?.sectionOptions?.logic || source?.sectionOptions?.logic;
    if (!source || !logic) return question;
    return {
      ...question,
      sectionOrder: question?.sectionOrder ?? source.sectionOrder,
      sectionOptions: {
        ...source.sectionOptions,
        ...question.sectionOptions,
        logic,
      },
    };
  });
};

const collectLogicRules = (logic) => {
  if (!logic) return [];
  if (Array.isArray(logic.groups)) {
    return logic.groups.flatMap((group) => group?.rules || []);
  }
  if (Array.isArray(logic.rules)) return logic.rules;
  return [];
};

export const logicReferencesSectionOrder = (logic, sectionOrder) => {
  const order = Number(sectionOrder);
  if (!Number.isFinite(order)) return false;
  return collectLogicRules(logic).some(
    (rule) => Number(rule?.sourceOrder) === order,
  );
};

const isSameQuestion = (left, right) => {
  if (!left || !right) return false;
  if (left === right) return true;
  if (left.id != null && left.id !== '' && left.id === right.id) return true;
  if (left.clientId && left.clientId === right.clientId) return true;
  return false;
};

/** Questions whose conditions check `question`, plus whether `question` has logic of its own. */
export const getQuestionDeleteLogicImpact = (questions = [], question) => {
  const hasOwnLogic = hasQuestionLogic(question);
  const dependents = questions.filter((item) => {
    if (!item || isSameQuestion(item, question)) return false;
    if (item.statusId === STATUSES.DELETE) return false;
    return logicReferencesSectionOrder(
      item?.sectionOptions?.logic,
      question?.sectionOrder,
    );
  });

  return {
    hasOwnLogic,
    dependents,
    needsWarning: hasOwnLogic || dependents.length > 0,
  };
};

/**
 * Remap logic rule sourceOrder values after questions are renumbered.
 * `orderMap` is Map<oldSectionOrder, newSectionOrder>.
 * A rule whose source question was removed is dropped. Other rules in the
 * same group stay, and keep following the question they already checked.
 * A rule that would point at the question itself is dropped too.
 * Returns null when no rules or page jump remain.
 */
export const remapLogicSourceOrders = (logic, orderMap, ownOrder) => {
  if (!logic || !orderMap?.size) return logic;

  const remapRules = (rules) => {
    let dropped = false;
    const nextRules = [];
    (rules || []).forEach((rule) => {
      if (rule?.sourceOrder == null || rule.sourceOrder === '') {
        nextRules.push(rule);
        return;
      }
      const nextOrder = orderMap.get(Number(rule.sourceOrder));
      if (
        nextOrder == null ||
        (ownOrder != null && Number(nextOrder) === Number(ownOrder))
      ) {
        dropped = true;
        return;
      }
      nextRules.push({ ...rule, sourceOrder: nextOrder });
    });
    return { rules: nextRules, dropped };
  };

  if (Array.isArray(logic.groups)) {
    const groups = [];
    logic.groups.forEach((group) => {
      const { rules, dropped } = remapRules(group?.rules);
      if (dropped && !rules.some((rule) => rule?.sourceOrder)) return;
      groups.push({ ...group, rules });
    });
    if (!groups.length && !logic.jumpToPageId) return null;
    return { ...logic, groups };
  }

  if (Array.isArray(logic.rules)) {
    const { rules, dropped } = remapRules(logic.rules);
    const remaining = dropped
      ? rules.filter((rule) => rule?.sourceOrder)
      : rules;
    if (!remaining.length && !logic.jumpToPageId) return null;
    return { ...logic, rules: remaining };
  }

  return logic;
};

export const getVisibleQuestions = (questions = [], values = {}) =>
  questions.filter(
    (question) =>
      !question?.sectionOptions?.internalOnly &&
      isQuestionVisible(question, values, questions),
  );

/** Build a rule patch when the operator changes (coerce value / matchCase). */
export const buildOperatorChangePatch = (operator, previousRule = {}) => {
  const preferred = canonicalizeOperator(operator) || 'equals';
  const valueType = getOperatorValueType(preferred);
  const patch = { operator: preferred };

  if (valueType === 'none') {
    patch.value = undefined;
  } else if (valueType === 'array') {
    patch.value = Array.isArray(previousRule.value) ? previousRule.value : [];
  } else if (valueType === 'range') {
    patch.value = Array.isArray(previousRule.value) && previousRule.value.length === 2
      ? previousRule.value
      : ['', ''];
  } else if (Array.isArray(previousRule.value)) {
    patch.value = previousRule.value[0] ?? '';
  }

  if (!operatorSupportsMatchCase(preferred)) {
    patch.matchCase = undefined;
  } else if (previousRule.matchCase === false) {
    patch.matchCase = false;
  }

  return patch;
};

/** Parse a free-text list into a string array for `in` / `notIn`. */
export const parseListInput = (text) =>
  String(text ?? '')
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);

export const formatListInput = (value) =>
  Array.isArray(value) ? value.join(', ') : '';
